// 定义请求方法的类型
import Util from "@/Master/Util";
import {defaultConfig, defaultRequestBody, getHeaderBasic, HttpConfig, HttpResponse} from "@/Fwk/Http/HttpConfig";
import {NetworkError} from "@/Fwk/Http/httpTypes";

export const comTag = 'WEB请求'

// 定义请求拦截器函数的类型
export type RequestInterceptor = (config: HttpConfig) => Promise<HttpConfig>;

// 定义响应拦截器函数的类型
export type ResDataInterceptor = (finalData: any,config: HttpConfig) => Promise<any>;

// 存储请求拦截器的数组
const requestInterceptors: RequestInterceptor[] = [];

// 存储响应拦截器的数组
const responseInterceptors: ResDataInterceptor[] = [];

// 注册请求拦截器的函数
export function registerRequestInterceptor(interceptor: RequestInterceptor) {
    requestInterceptors.push(interceptor);
}

// 注册响应拦截器的函数
export function registerResponseInterceptor(interceptor: ResDataInterceptor) {
    responseInterceptors.push(interceptor);
}

// 通用请求函数
export async function comRequest<T>(config: HttpConfig): Promise<HttpResponse<T>> {
    const {
        url,
        requestCount = defaultConfig.requestCount,
        encryptionType = defaultConfig.encryptionType,
        isToken = defaultConfig.isToken,
        baseUrl = defaultConfig.baseUrl!,
        method = defaultConfig.method!,
        headers = {},
        body = defaultConfig.body,
        timeout = defaultConfig.timeout!,
        retry = defaultConfig.retry!,
        retryDelay = defaultConfig.retryDelay!,
        signal,
        isMergeBody=defaultConfig.isMergeBody,
        isBasic=defaultConfig.isBasic,
        isSerErrTips=defaultConfig.isSerErrTips,
    } = config;

    // 创建 AbortController 实例
    const controller = new AbortController();
    const finalSignal = signal || controller.signal;

    // 设置请求超时
    const timeoutId = setTimeout(() => {
        controller.abort();
    }, timeout);

    // 合并请求体Body
    let requestBody = null;
    if (isMergeBody && body) {
        let newRequestBody = Util.DeepClone<any>(defaultRequestBody);
        if (encryptionType == 'RSA'){
            newRequestBody.request_data = { ...newRequestBody.request_data, ...body };
            delete newRequestBody.requestData;
            if (isBasic){
                newRequestBody.request_data = { ...newRequestBody.request_data, ...getHeaderBasic() };
            }
        }else{
            newRequestBody.requestData = { ...newRequestBody.requestData, ...body };
            delete newRequestBody.request_data;
            if (isBasic){
                newRequestBody.requestData = { ...newRequestBody.requestData, ...getHeaderBasic() };
            }
        }
        requestBody = newRequestBody;
    }else{
        requestBody = body;
    }

    // 合并配置
    let mergedConfig: HttpConfig = {
        requestCount,
        url,
        encryptionType,
        baseUrl,
        method,
        headers: { ...defaultConfig.headers, ...headers },
        body: requestBody,
        timeout,
        retry,
        retryDelay,
        signal: finalSignal,
        isToken: isToken,
    };

    // 执行请求拦截器
    for (const interceptor of requestInterceptors) {
        mergedConfig = await interceptor(mergedConfig);
    }

    let attempt = 0;

    while (attempt <= mergedConfig.retry!) {
        try {
            // 发起 fetch 请求
            const response = await fetch(`${mergedConfig.baseUrl}${mergedConfig.url}`, {
                method: mergedConfig.method,
                headers: mergedConfig.headers,
                body: mergedConfig.body,
                signal: mergedConfig.signal,
            });

            const clonedResponse = response.clone();
             // 解析json只在这里进行一次
            let finalData = await clonedResponse.json();
            // 将原response和解析后的数据对象传递给拦截器
            for (const interceptor of responseInterceptors) {
                finalData = await interceptor(finalData,mergedConfig);
            }

            clearTimeout(timeoutId);

            const {code, data, message, timestamp,description,etag} = finalData;
            return {
                code:code,
                data:data as T,
                etag:etag,
                message:message,
                description:description,
                timestamp:timestamp,
            };
        } catch (error: any) {
            if (error?.name === 'AbortError') {
                if (Util.isLog) {
                    console.error(comTag,'请求被取消或超时:');
                }
                break;
            }

            if (attempt < mergedConfig.retry!) {
                attempt++;
                if (Util.isLog) {
                    console.warn(comTag,`请求失败，尝试第 ${attempt} 次重试...`);
                }
                await new Promise(res => setTimeout(res, mergedConfig.retryDelay));
                continue;
            }
            if (Util.isLog) {
                console.error(comTag,`网络请求出现错误:`,error);
            }
            throw error;
        }
    }

    if (Util.isLog) {
        console.error(comTag,`请求失败:`,mergedConfig);
    }
    throw new NetworkError("NetworkError");
}
